import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { resolveImage } from "@/lib/api";
import { PriceBlock } from "@/components/PriceBlock";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";

const categoryLabel = { Gold: "9K Gold", Silver: "925 Silver", GP: "Gold Plated" };

export const ProductCard = ({ product, index = 0 }) => {
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const outOfStock = product.stock <= 0;

  const handleAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || user === false) {
      toast.info("Please login to add items to your cart");
      navigate("/login");
      return;
    }
    if (user.role === "admin") {
      toast.info("Switch to a customer account to shop");
      return;
    }
    try {
      await addToCart(product.id, 1);
    } catch (err) {
      toast.error("Could not add to cart");
    }
  };

  return (
    <div className="group animate-fade-up" style={{ animationDelay: `${index * 40}ms` }} data-testid={`product-card-${product.sku}`}>
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative overflow-hidden bg-white border border-black/10 aspect-[4/5]">
          <img
            src={resolveImage(product.images?.[0])}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          {product.discount_percent > 0 && (
            <span className="absolute top-3 left-3 bg-discount text-white text-[11px] font-semibold uppercase tracking-wider px-2 py-1">
              {product.discount_percent}% OFF
            </span>
          )}
          {outOfStock && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <span className="overline text-obsidian bg-white px-3 py-1 border border-black/10">Out of Stock</span>
            </div>
          )}
          <span className="absolute top-3 right-3 bg-obsidian/90 text-white text-[10px] uppercase tracking-wider px-2 py-1">
            {categoryLabel[product.category] || product.category}
          </span>
        </div>
      </Link>

      <div className="pt-4">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-[11px] tracking-wider text-muted-foreground uppercase">{product.sku}</span>
          {product.weight && <span className="text-[11px] text-muted-foreground">{product.weight}</span>}
        </div>
        <Link to={`/product/${product.id}`}>
          <h3 className="font-serif text-xl leading-tight text-obsidian hover:text-gold-dark transition-colors mb-2">
            {product.name}
          </h3>
        </Link>
        <PriceBlock mrp={product.mrp} price={product.price} discount={product.discount_percent} size="md" />

        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={handleAdd}
            disabled={outOfStock}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-obsidian text-white text-sm font-semibold py-2.5 hover:bg-gold hover:text-obsidian transition-colors disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
            data-testid={`add-to-cart-${product.sku}`}
          >
            <ShoppingBag className="w-4 h-4" /> Add to Cart
          </button>
          <Link
            to={`/product/${product.id}`}
            className="px-4 py-2.5 text-sm font-semibold border border-obsidian text-obsidian hover:bg-obsidian hover:text-white transition-colors rounded-sm"
            data-testid={`view-details-${product.sku}`}
          >
            View
          </Link>
        </div>
      </div>
    </div>
  );
};
